import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  useCreateMissingEmployeeRateMutation,
  useGetMissingEmployeeRatesMutation,
} from '../../api/gridApi';
import type {
  CreateMissingEmployeeRateRequest,
  MissingEmployeeRateCurrencyOption,
  MissingEmployeeRateItem,
} from '../../types';
import { extractErrorMessage } from '../../../../utils/errorUtils';

type MissingEmployeeRatesDialogProps = {
  open: boolean;
  onClose: () => void;
  onRateCreated: (message: string) => void;
};

type AddRateDialogProps = {
  row: MissingEmployeeRateItem;
  currencies: MissingEmployeeRateCurrencyOption[];
  onClose: () => void;
  onCreated: (message: string) => void;
};

const missingCellSx = { color: 'error.main', fontWeight: 600 };

const getDateInputValue = (value: string): string => {
  return value.trim().match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? '';
};

const AddRateDialog = ({ row, currencies, onClose, onCreated }: AddRateDialogProps) => {
  const [rate, setRate] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [rateStartDate, setRateStartDate] = useState(getDateInputValue(row.rateStartDateDefault));
  const [validationError, setValidationError] = useState<string | null>(null);
  const [createRate, createState] = useCreateMissingEmployeeRateMutation();

  const errorMessage =
    validationError ||
    (createState.error
      ? extractErrorMessage(createState.error, 'Failed to create employee rate.')
      : null);

  const handleSave = async () => {
    const normalizedRate = rate.trim();
    const parsedRate = Number(normalizedRate);

    if (!normalizedRate || !Number.isFinite(parsedRate) || parsedRate <= 0) {
      setValidationError('Rate must be greater than 0.');
      return;
    }

    if (!currencyId) {
      setValidationError('Currency is required.');
      return;
    }

    if (!rateStartDate) {
      setValidationError('Rate Start Date is required.');
      return;
    }

    setValidationError(null);

    const request: CreateMissingEmployeeRateRequest = {
      clientId: row.clientId,
      employeeId: row.employeeId,
      rate: normalizedRate,
      currencyId: Number(currencyId),
      rateStartDate,
    };

    try {
      const result = await createRate(request).unwrap();
      onCreated(result.message || 'Rate added successfully.');
    } catch {
      // The mutation error is displayed in the dialog.
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Rate</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <TextField label="Client" value={row.clientName} InputProps={{ readOnly: true }} />
          <TextField label="Employee" value={row.employeeName} InputProps={{ readOnly: true }} />
          <TextField
            required
            autoFocus
            label="Rate"
            type="number"
            value={rate}
            inputProps={{ min: 0, step: 'any' }}
            onChange={(event) => {
              setValidationError(null);
              setRate(event.target.value);
            }}
            disabled={createState.isLoading}
          />
          <FormControl required fullWidth disabled={createState.isLoading}>
            <InputLabel id="missing-rate-currency-label">Currency</InputLabel>
            <Select
              labelId="missing-rate-currency-label"
              label="Currency"
              value={currencyId}
              onChange={(event) => {
                setValidationError(null);
                setCurrencyId(event.target.value);
              }}
            >
              {currencies.map((currency) => (
                <MenuItem key={currency.currencyId} value={String(currency.currencyId)}>
                  {currency.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            required
            label="Rate Start Date"
            type="date"
            value={rateStartDate}
            onChange={(event) => {
              setValidationError(null);
              setRateStartDate(event.target.value);
            }}
            InputLabelProps={{ shrink: true }}
            disabled={createState.isLoading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createState.isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={createState.isLoading}>
          {createState.isLoading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const MissingEmployeeRatesDialog = ({
  open,
  onClose,
  onRateCreated,
}: MissingEmployeeRatesDialogProps) => {
  const [excludedClientIds, setExcludedClientIds] = useState<number[]>([]);
  const [selectedRow, setSelectedRow] = useState<MissingEmployeeRateItem | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadMissingRates, loadState] = useGetMissingEmployeeRatesMutation();

  const reload = useCallback(async () => {
    setLoadError(null);
    try {
      await loadMissingRates({ excludedClientIds }).unwrap();
    } catch (error) {
      setLoadError(extractErrorMessage(error, 'Failed to load missing employee rates.'));
    }
  }, [excludedClientIds, loadMissingRates]);

  useEffect(() => {
    if (!open) return undefined;

    const reloadTimeout = window.setTimeout(() => {
      void reload();
    }, 0);

    return () => window.clearTimeout(reloadTimeout);
  }, [open, reload]);

  const result = loadState.data;
  const rows = result?.rows ?? [];
  const clients = result?.availableClients ?? [];
  const selectedClients = clients.filter((client) => excludedClientIds.includes(client.id));
  const errorMessage =
    loadError ||
    (loadState.error
      ? extractErrorMessage(loadState.error, 'Failed to load missing employee rates.')
      : null);

  const handleRateCreated = async (message: string) => {
    setSelectedRow(null);
    onRateCreated(message);
    await reload();
  };

  const handleClose = () => {
    setSelectedRow(null);
    setExcludedClientIds([]);
    setLoadError(null);
    loadState.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>Missing Employee Rates for Clients</DialogTitle>
      <DialogContent dividers sx={{ minHeight: 520 }}>
        <Stack spacing={2} sx={{ height: '100%' }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Autocomplete
            multiple
            options={clients}
            value={selectedClients}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, values) => setExcludedClientIds(values.map((client) => client.id))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Excluded Clients"
                placeholder="Select excluded clients"
              />
            )}
            disabled={loadState.isLoading}
          />

          <TableContainer sx={{ border: 1, borderColor: 'divider', flex: 1, overflowX: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell align="right">Billable Hours</TableCell>
                  <TableCell sx={{ minWidth: 135, whiteSpace: 'nowrap' }}>Period</TableCell>
                  <TableCell sx={{ minWidth: 135, whiteSpace: 'nowrap' }}>
                    First Billable Date
                  </TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Rate Start Date</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadState.isLoading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!loadState.isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No missing employee rates found.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.rowKey} hover>
                    <TableCell>{row.clientName}</TableCell>
                    <TableCell>{row.employeeName}</TableCell>
                    <TableCell align="right">{row.billableHours.toFixed(2)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.period}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.firstBillableDate}</TableCell>
                    <TableCell>
                      <Typography component="span" sx={missingCellSx}>
                        Missing
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={missingCellSx}>
                        Missing
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={missingCellSx}>
                        Missing
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="contained" onClick={() => setSelectedRow(row)}>
                        Add Rate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>

      {selectedRow && (
        <AddRateDialog
          key={selectedRow.rowKey}
          row={selectedRow}
          currencies={result?.currencies ?? []}
          onClose={() => setSelectedRow(null)}
          onCreated={handleRateCreated}
        />
      )}
    </Dialog>
  );
};

export default MissingEmployeeRatesDialog;
